import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { addDays, startOfMonth, startOfWeek, format } from 'date-fns';
import AuthContext from '../contexts/AuthContext';
import { useAxios } from './useAxios';
import {
  mapAllocationsByDay,
  mapAvailabilitiesByDay,
  mergeDayMaps,
} from '../utils/employeeCalendarUtils';

const getMonthsForWeek = (weekStart) => {
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  const seen = new Set();
  const months = [];

  weekDays.forEach((day) => {
    const monthStart = startOfMonth(day);
    const key = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
    if (seen.has(key)) return;
    seen.add(key);
    months.push({
      month: monthStart.getMonth() + 1,
      year: monthStart.getFullYear(),
    });
  });

  return months;
};

export const useEmployeeCalendarData = (weekStart) => {
  const { token } = useContext(AuthContext);
  const { sendRequest } = useAxios();

  const [availabilities, setAvailabilities] = useState({});
  const [offeredAllocations, setOfferedAllocations] = useState({});
  const [openAllocations, setOpenAllocations] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );

  const monthsToFetch = useMemo(() => getMonthsForWeek(weekStart), [weekStart]);

  const refresh = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const responses = await Promise.all(
        monthsToFetch.map(({ month, year }) =>
          sendRequest(`/employeeAvailabilities/${month}/${year}`, 'GET', null, {
            Authorization: `Bearer ${token}`,
          })
        )
      );

      let mergedAvailabilities = {};
      let mergedOffered = {};
      let mergedOpen = {};

      responses.forEach((response) => {
        mergedAvailabilities = mergeDayMaps(
          mergedAvailabilities,
          mapAvailabilitiesByDay(response?.employeeAvailabilities)
        );
        mergedOffered = mergeDayMaps(
          mergedOffered,
          mapAllocationsByDay(response?.offeredShiftAllocations)
        );
        mergedOpen = mergeDayMaps(mergedOpen, mapAllocationsByDay(response?.openShiftAllocations));
      });

      setAvailabilities(mergedAvailabilities);
      setOfferedAllocations(mergedOffered);
      setOpenAllocations(mergedOpen);
    } catch (error) {
      console.error('Error fetching employee calendar data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, sendRequest, monthsToFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const visibleMonth = useMemo(() => startOfMonth(weekStart), [weekStart]);

  return {
    availabilities,
    offeredAllocations,
    openAllocations,
    refresh,
    isLoading,
    weekDays,
    visibleMonth,
  };
};

export const getMondayWeekStart = (date) => startOfWeek(date, { weekStartsOn: 1 });

export const formatDayKey = (day) => format(day, 'yyyy-MM-dd');
